import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { registerUser, loginUser } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import Logo from "../components/Logo";
import { ArrowRight, Eye, EyeOff } from "lucide-react";

function formatError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function AuthPage() {
  const [mode, setMode] = useState("login"); // login | register
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { setUser } = useAuth();

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogle = () => {
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = mode === "register"
        ? await registerUser(email, password, name)
        : await loginUser(email, password);
      const data = res.data;
      setUser(data);
      // New email registrations need to verify; redirect to inbox-check page
      if (mode === "register" && data.email_verified === false) {
        navigate("/verify-email", { replace: true });
        return;
      }
      if (!data.role) {
        navigate("/select-role", { replace: true });
      } else if (data.role === "landlord") {
        navigate("/landlord-dashboard", { replace: true });
      } else if (data.role === "admin") {
        navigate("/admin", { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    } catch (err) {
      // Log full error to console so we can debug client reports
      console.error(`[Auth ${mode} failed]`, err);
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (detail) {
        setError(formatError(detail));
      } else if (status) {
        setError(`Server error (${status}). Please try again in a moment.`);
      } else if (err?.code === "ERR_NETWORK" || err?.message?.toLowerCase().includes("network")) {
        setError("Can't reach the server. Check your internet connection and try again.");
      } else {
        setError(err?.message || "Connection failed. Please refresh and try again.");
      }
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: "#faf8f3" }}>
      {/* Nav */}
      <nav className="px-5 md:px-12 py-4 flex items-center justify-between border-b border-black/[0.06]">
        <Logo />
        <Link to="/browse" className="text-sm font-medium text-[#0d0d0d] hover:text-[#1a6b3c]" data-testid="auth-nav-browse">
          Browse
        </Link>
      </nav>

      {/* Auth Card */}
      <div className="flex-1 flex items-center justify-center px-5 py-12">
        <div className="w-full max-w-md">
          <h1 className="text-3xl sm:text-4xl font-black text-[#0d0d0d] font-['Fraunces'] mb-2" data-testid="auth-heading">
            {mode === "login" ? "Welcome back" : "Create your account"}
          </h1>
          <p className="text-[#888] mb-8 text-sm">
            {mode === "login"
              ? "Sign in to find your next place."
              : "Join Low Key Rents to get started."}
          </p>

          {/* Google Button */}
          <button
            data-testid="google-signin-btn"
            onClick={handleGoogle}
            className="w-full flex items-center justify-center gap-3 bg-white border border-black/[0.1] rounded-xl h-12 text-sm font-semibold text-[#0d0d0d] hover:bg-[#faf8f3] transition-colors mb-4"
          >
            <svg width="18" height="18" viewBox="0 0 18 18"><path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/><path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/><path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/><path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/></svg>
            Continue with Google
          </button>

          {/* Divider */}
          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-black/[0.08]" />
            <span className="text-xs text-[#888] font-medium">or</span>
            <div className="flex-1 h-px bg-black/[0.08]" />
          </div>

          {/* Email/Password Form */}
          <form onSubmit={handleSubmit} className="space-y-3" data-testid="auth-form">
            {mode === "register" && (
              <div>
                <label className="text-xs font-medium text-[#0d0d0d] mb-1 block">Full Name</label>
                <Input
                  data-testid="auth-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Doe"
                  className="rounded-xl border-black/[0.1] h-11"
                  required
                />
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-[#0d0d0d] mb-1 block">Email</label>
              <Input
                data-testid="auth-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="rounded-xl border-black/[0.1] h-11"
                required
              />
            </div>
            <div>
              <label className="text-xs font-medium text-[#0d0d0d] mb-1 block">Password</label>
              <div className="relative">
                <Input
                  data-testid="auth-password-input"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={mode === "register" ? "At least 6 characters" : "Your password"}
                  className="rounded-xl border-black/[0.1] h-11 pr-10"
                  required
                  minLength={mode === "register" ? 6 : 1}
                />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#888]">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {error && (
              <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2" data-testid="auth-error">
                {error}
              </p>
            )}

            <Button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-xl bg-[#1a6b3c] hover:bg-[#15562f] text-white font-semibold text-sm"
            >
              {loading
                ? "Please wait..."
                : mode === "login"
                  ? "Sign In"
                  : "Create Account"}
              {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
            </Button>

            {mode === "login" && (
              <div className="text-right">
                <a href="/forgot-password" className="text-xs text-[#1a6b3c] font-medium hover:underline" data-testid="forgot-password-link">
                  Forgot password?
                </a>
              </div>
            )}
          </form>

          {/* Toggle */}
          <p className="text-center text-sm text-[#888] mt-6">
            {mode === "login" ? (
              <>
                Don't have an account?{" "}
                <button data-testid="switch-to-register" onClick={() => { setMode("register"); setError(""); }} className="text-[#1a6b3c] font-semibold hover:underline">
                  Sign up
                </button>
              </>
            ) : (
              <>
                Already have an account?{" "}
                <button data-testid="switch-to-login" onClick={() => { setMode("login"); setError(""); }} className="text-[#1a6b3c] font-semibold hover:underline">
                  Sign in
                </button>
              </>
            )}
          </p>

          <p className="text-center text-[11px] text-[#aaa] mt-4">
            By continuing, you agree to our{" "}
            <a href="/terms" className="text-[#1a6b3c] hover:underline">Terms of Service</a>{" "}and{" "}
            <a href="/privacy" className="text-[#1a6b3c] hover:underline">Privacy Policy</a>.
          </p>
        </div>
      </div>
    </div>
  );
}
